import { useState } from 'react';
import {
  SimpleForm,
  Toolbar,
  useNotify,
  useRecordContext,
  useRedirect,
} from 'react-admin';
import { useParams } from 'react-router-dom';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  Grid,
  Paper,
  Radio,
  RadioGroup,
  Table,
  TableContainer,
  Typography,
} from '@mui/material';

import { usePetByUser } from '@pumpkincare/users';

import { convertCentsToDollars } from '../../../../shared/utils';
import {
  updateBillingFrequency,
  validateBillingFrequencyChange,
} from '../../service/customerService';
import BillingFrequencyPetInfo from './billing-frequency-pet-info';

const BILLING_FREQUENCY_OPTIONS = {
  'monthly-to-annual-midterm': {
    _id: 'monthly-to-annual-midterm',
    change_type: 'MONTHLY_TO_ANNUAL',
    new_frequency: 'annual',
    plan_status: 'active',
    title: 'Monthly to Annual Midterm',
  },
  'annual-to-monthly-renewal': {
    _id: 'annual-to-monthly-renewal',
    change_type: 'ANNUAL_TO_MONTHLY',
    new_frequency: 'monthly',
    plan_status: 'renew_pending',
    title: 'Annual to Monthly Renewal',
  },
  'monthly-to-annual-renewal': {
    _id: 'monthly-to-annual-renewal',
    change_type: 'MONTHLY_TO_ANNUAL',
    new_frequency: 'annual',
    plan_status: 'renew_pending',
    title: 'Monthly to Annual Renewal',
  },
};

const DISPLAY_STATUSES = ['renew_pending', 'active', 'lapsed', 'pending'];

function BillingFrequency() {
  const { id } = useParams();
  const notify = useNotify();
  const redirect = useRedirect();
  const userRecord = useRecordContext();
  const customerBillingPageUrl = `/customers/${id}/show/billing`;
  const customerDetailsUrl = `/customers/${id}/show`;

  const [isUpdateInProgress, setIsUpdateInProgress] = useState(false);
  const [isChangeValidated, setIsChangeValidated] = useState(false);
  const [invoiceTotal, setInvoiceTotal] = useState(0);
  const [validPetIds, setValidPetIds] = useState([]);
  const [billingFrequencyChangeOption, setBillingFrequencyChangeOption] =
    useState(null);
  const [updatedPetData, setUpdatedPetData] = useState(null);
  const [expandedRows, setExpandedRows] = useState([]);
  const { data: petData, isLoading } = usePetByUser(id, {
    onSuccess: data => formatPetsRows(data.data, null),
  });

  const dataHasRenewPendingPolicies = updatedPetData?.some(pet =>
    pet.policies.some(policy => policy.status === 'renew_pending')
  );
  const dataHasActivePolicies = updatedPetData?.some(pet =>
    pet.policies.some(policy => policy.status === 'active')
  );
  const dataHasInvalidPolicies = updatedPetData?.some(pet =>
    pet.policies.some(
      policy => policy.status === 'lapsed' || policy.status === 'pending'
    )
  );

  function shouldDisableOption(option) {
    const isChargedAnnually = userRecord.is_charged_annually;
    if (dataHasRenewPendingPolicies) {
      if (option.includes('renewal')) {
        return isChargedAnnually
          ? option !== 'annual-to-monthly-renewal'
          : option !== 'monthly-to-annual-renewal';
      } else {
        return true;
      }
    } else if (dataHasActivePolicies) {
      if (!option.includes('midterm')) {
        return true;
      }
      return isChargedAnnually;
    }
    return true;
  }

  function formatPetsRows(data, newData) {
    if (data) {
      const filteredPetData = data.filter(pet =>
        pet.policies.some(policy => DISPLAY_STATUSES.includes(policy.status))
      );

      const updatedData = filteredPetData.map(pet => {
        if (newData) {
          const invoiceAmounts = newData?.invoice_amounts ?? [];
          const petInvoice = invoiceAmounts.find(
            invoice => invoice.pet_id === pet.id
          );
          const policyInvoiceTotal = petInvoice?.policy_total ?? 0;
          const petPlanInvoiceTotal = petInvoice?.pet_plan_total ?? 0;

          const newFrequency =
            BILLING_FREQUENCY_OPTIONS[billingFrequencyChangeOption]?.new_frequency ||
            '';

          return {
            ...pet,
            policy_invoice_total: policyInvoiceTotal,
            pet_plan_invoice_total: petPlanInvoiceTotal,
            new_billing_frequency: newFrequency,
          };
        } else {
          return pet;
        }
      });

      const sortedData = updatedData.sort((a, b) => {
        const aStatus = a.policies
          .filter(policy => DISPLAY_STATUSES.includes(policy.status))
          .sort((a, b) => b.year - a.year)[0].status;
        const bStatus = b.policies
          .filter(policy => DISPLAY_STATUSES.includes(policy.status))
          .sort((a, b) => b.year - a.year)[0].status;
        return DISPLAY_STATUSES.indexOf(aStatus) - DISPLAY_STATUSES.indexOf(bStatus);
      });

      setUpdatedPetData(sortedData);
      setExpandedRows(sortedData.map((_, index) => index));
    }
  }

  function handleValidateButtonClick() {
    if (!billingFrequencyChangeOption) return;
    const selectedOption = BILLING_FREQUENCY_OPTIONS[billingFrequencyChangeOption];
    if (selectedOption) {
      const changeType = selectedOption.change_type;
      const planStatus = selectedOption.plan_status;

      validateBillingFrequencyChange(id, { changeType, planStatus })
        .then(response => {
          if (response.data) {
            const validatedData = response.data;
            if (validatedData.data.invoice_total) {
              setInvoiceTotal(validatedData.data.invoice_total);
            }
            formatPetsRows(petData, validatedData.data);
            setIsChangeValidated(true);
            setValidPetIds(validatedData.data.pet_ids || []);
            notify('Billing frequency change validated successfully.', {
              type: 'info',
            });
          }
        })
        .catch(error => {
          notify(error.message, {
            type: 'error',
          });
        });
    }
  }

  function handleApplyButtonClick() {
    if (!isChangeValidated) return;
    if (validPetIds.length === 0) {
      notify('No valid pets found for this billing frequency change.', {
        type: 'warning',
      });
      return;
    }

    const selectedOption = BILLING_FREQUENCY_OPTIONS[billingFrequencyChangeOption];

    if (selectedOption) {
      const changeType = selectedOption.change_type;
      const planStatus = selectedOption.plan_status;
      const profileId = petData[0]?.user.profile_id;

      setIsUpdateInProgress(true);
      updateBillingFrequency(id, {
        changeType,
        planStatus,
        validPetIds,
        profileId,
      })
        .then(() => {
          notify('Billing frequency change applied successfully.', {
            type: 'info',
          });
          if (planStatus === 'renew_pending') {
            redirect(customerDetailsUrl);
          } else {
            redirect(customerBillingPageUrl);
          }
          setIsUpdateInProgress(false);
        })
        .catch(error => {
          notify(`Update failed: ${error.message} Please try again later.`, {
            type: 'error',
          });
          setIsUpdateInProgress(false);
        });
    }
  }

  function handleRadioOptionChange(value) {
    if (billingFrequencyChangeOption && isChangeValidated) {
      formatPetsRows(petData, null);
      setInvoiceTotal(0);
    }
    setBillingFrequencyChangeOption(value);
    setIsChangeValidated(false);
  }

  const billingFrequencyToolbar = (
    <Toolbar sx={{ background: '#FFFFFF' }}>
      <Button
        variant='contained'
        color='secondary'
        label='Validate'
        disabled={
          !billingFrequencyChangeOption ||
          isUpdateInProgress ||
          dataHasInvalidPolicies
        }
        onClick={() => {
          handleValidateButtonClick();
        }}
        size='large'
        sx={{ marginRight: '10px' }}
      >
        Validate
      </Button>
      <Button
        variant='contained'
        color='secondary'
        label='Apply'
        size='large'
        disabled={
          !isChangeValidated ||
          isUpdateInProgress ||
          validPetIds.length === 0 ||
          dataHasInvalidPolicies
        }
        onClick={() => {
          handleApplyButtonClick();
        }}
      >
        Apply
      </Button>
    </Toolbar>
  );

  if (isLoading || isUpdateInProgress) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', marginTop: '20px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <SimpleForm toolbar={billingFrequencyToolbar} onSubmit={null}>
      {dataHasInvalidPolicies ? (
        <Alert
          icon={<InfoOutlinedIcon fontSize='inherit' />}
          variant='filled'
          sx={{ backgroundColor: '#FF9800', width: '100%' }}
        >
          Billing Frequencies cannot be changed when pets have lapsed or pending
          policies.
        </Alert>
      ) : null}
      <Grid
        container
        direction='row'
        justifyContent='flex-start'
        alignItems='flex-start'
        spacing={3}
        sx={{ marginBottom: '10px' }}
      >
        <Grid item>
          <RadioGroup
            value={billingFrequencyChangeOption}
            onChange={e => handleRadioOptionChange(e.target.value)}
            row
          >
            {Object.keys(BILLING_FREQUENCY_OPTIONS).map(option => {
              const optionData = BILLING_FREQUENCY_OPTIONS[option];
              return (
                <FormControlLabel
                  key={optionData._id}
                  value={optionData._id}
                  control={<Radio />}
                  label={optionData.title}
                  disabled={shouldDisableOption(optionData._id)}
                />
              );
            })}
          </RadioGroup>
        </Grid>
      </Grid>
      <Grid
        container
        direction='row'
        justifyContent='flex-start'
        alignItems='flex-start'
        spacing={3}
      >
        <Grid item xs={12}>
          <TableContainer
            component={Paper}
            sx={{ height: '500px', overflowY: 'auto' }}
          >
            <Table stickyHeader>
              <BillingFrequencyPetInfo
                petData={updatedPetData}
                expandedRows={expandedRows}
                setExpandedRows={setExpandedRows}
                displayStatuses={DISPLAY_STATUSES}
              />
            </Table>
          </TableContainer>
          {invoiceTotal > 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'end', marginTop: '20px' }}>
              <Typography
                fontSize={20}
                data-test-id='billing-freq-change-invoice-total'
              >
                Total Owed: ${convertCentsToDollars(invoiceTotal)}
              </Typography>
            </Box>
          ) : null}
        </Grid>
      </Grid>
    </SimpleForm>
  );
}

export default BillingFrequency;
